import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, Phone, MessageCircle, Mail, Briefcase, Globe, Building2, Home, DollarSign, Bed, Bath, MapPin, MoreVertical, X, Edit, Eye, Clock, CheckCircle2, Calendar, FileText, Save, Plus, AlertCircle, ChevronDown, ChevronUp, User, Sparkles, TrendingUp, Target, Lightbulb, Mic, MicOff } from 'lucide-react';
import { PropertyCard } from '../components/PropertyCard';
import { BottomNav } from '../components/BottomNav';
interface ClientDetailPageProps {
  clientId: number;
  onBack: () => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
}
interface TimelineEvent {
  id: number;
  type: string;
  title: string;
  description: string;
  date: string;
  timestamp: Date;
  status: 'past' | 'overdue' | 'upcoming';
  icon: any;
  color: string;
  user: {
    name: string;
    initials: string;
    avatar?: string;
  };
}
interface Property {
  id: number;
  title: string;
  price: string;
  location: string;
  beds: number;
  baths: number;
  area: string;
  matchPercentage: number;
  status: 'new' | 'sent' | 'archived';
  image?: string;
}
type CriteriaStatus = 'active' | 'on-hold' | 'confirmed' | 'negotiating' | 'closed';
interface SearchCriteria {
  group: string;
  type: string;
  budget: string;
  beds: number;
  baths: number;
  location: string;
  status: CriteriaStatus;
}
// Speech Recognition types
interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}
interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}
export function ClientDetailPage({
  clientId,
  onBack,
  onNetworkClick,
  onInventoryClick,
  onRMIClick
}: ClientDetailPageProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showEditCriteriaModal, setShowEditCriteriaModal] = useState(false);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<CriteriaStatus | null>(null);
  const [activeTab, setActiveTab] = useState<'timeline' | 'matched'>('timeline');
  const [activityModalStep, setActivityModalStep] = useState<'type' | 'mode' | 'form'>('type');
  const [selectedActivityType, setSelectedActivityType] = useState<'call' | 'meeting' | 'site-visit'>('call');
  const [activityMode, setActivityMode] = useState<'log' | 'schedule'>('log');
  const [activityJustLogged, setActivityJustLogged] = useState(false);
  const [noteJustAdded, setNoteJustAdded] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState<any>(null);
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  // Note form state
  const [noteText, setNoteText] = useState('');
  const [isRecordingNote, setIsRecordingNote] = useState(false);
  const [interimNoteTranscript, setInterimNoteTranscript] = useState('');
  const noteRecognitionRef = useRef<SpeechRecognition | null>(null);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Initialize speech recognition for activity notes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognitionAPI) {
        const recognition = new SpeechRecognitionAPI();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let finalTranscript = '';
          let interim = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interim += transcript;
            }
          }
          if (finalTranscript) {
            setNewActivity(prev => ({
              ...prev,
              note: prev.note + finalTranscript
            }));
          }
          setInterimTranscript(interim);
        };
        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsRecording(false);
          setInterimTranscript('');
          if (event.error === 'not-allowed') {
            alert('Microphone access denied. Please allow microphone access to use voice input.');
          }
        };
        recognition.onend = () => {
          setIsRecording(false);
          setInterimTranscript('');
        };
        recognitionRef.current = recognition;
      }
    }
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);
  // Initialize speech recognition for standalone notes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognitionAPI) {
        const recognition = new SpeechRecognitionAPI();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let finalTranscript = '';
          let interim = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interim += transcript;
            }
          }
          if (finalTranscript) {
            setNoteText(prev => prev + finalTranscript);
          }
          setInterimNoteTranscript(interim);
        };
        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsRecordingNote(false);
          setInterimNoteTranscript('');
          if (event.error === 'not-allowed') {
            alert('Microphone access denied. Please allow microphone access to use voice input.');
          }
        };
        recognition.onend = () => {
          setIsRecordingNote(false);
          setInterimNoteTranscript('');
        };
        noteRecognitionRef.current = recognition;
      }
    }
    return () => {
      if (noteRecognitionRef.current) {
        noteRecognitionRef.current.abort();
      }
    };
  }, []);
  const toggleRecording = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }
    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
      setInterimTranscript('');
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        alert('Could not start voice recording. Please try again.');
      }
    }
  };
  const toggleNoteRecording = () => {
    if (!noteRecognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }
    if (isRecordingNote) {
      noteRecognitionRef.current.stop();
      setIsRecordingNote(false);
      setInterimNoteTranscript('');
    } else {
      try {
        noteRecognitionRef.current.start();
        setIsRecordingNote(true);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        alert('Could not start voice recording. Please try again.');
      }
    }
  };
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    overdue: true,
    upcoming: true,
    past: true
  });
  // Property sections state
  const [expandedPropertySections, setExpandedPropertySections] = useState({
    new: true,
    sent: true,
    archived: true
  });
  // Logged in user
  const loggedUser = {
    name: 'Sarah Ahmed',
    initials: 'SA',
    avatar: undefined
  };
  // New activity form state
  const [newActivity, setNewActivity] = useState({
    type: 'call',
    date: '',
    time: '',
    note: ''
  });
  // Predefined statuses from database
  const criteriaStatuses: {
    value: CriteriaStatus;
    label: string;
    color: string;
    bgColor: string;
  }[] = [{
    value: 'active',
    label: 'Active',
    color: 'text-green-700',
    bgColor: 'bg-green-100'
  }, {
    value: 'on-hold',
    label: 'On Hold',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100'
  }, {
    value: 'confirmed',
    label: 'Confirmed',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100'
  }, {
    value: 'negotiating',
    label: 'Negotiating',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100'
  }, {
    value: 'closed',
    label: 'Closed',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100'
  }];
  // Activity types
  const activityTypes = [{
    value: 'call',
    label: 'Call',
    icon: Phone
  }, {
    value: 'meeting',
    label: 'Meeting',
    icon: Calendar
  }, {
    value: 'site-visit',
    label: 'Site Visit',
    icon: Home
  }];
  // Mock data based on the selected client
  const [client, setClient] = useState({
    name: 'Ahmed Hamdy Mohamed',
    phone: '+20 123 456 7890',
    email: 'ahmed.hamdy@example.com',
    position: 'Real Estate Investor',
    source: 'Website Lead',
    criteria: {
      group: 'Residential',
      type: 'Villa',
      budget: '2M - 2.5M EGP',
      beds: 3,
      baths: 2,
      location: 'New Cairo, 5th Settlement',
      status: 'active' as CriteriaStatus
    } as SearchCriteria
  });
  // Edit form state
  const [editForm, setEditForm] = useState(client.criteria);
  const handleSaveCriteria = () => {
    setClient({
      ...client,
      criteria: editForm
    });
    setShowEditCriteriaModal(false);
  };
  const handleStatusChange = (newStatus: CriteriaStatus) => {
    setPendingStatus(newStatus);
    setShowStatusDropdown(false);
    setShowStatusConfirmModal(true);
  };
  const handleConfirmStatusChange = () => {
    if (pendingStatus) {
      setClient({
        ...client,
        criteria: {
          ...client.criteria,
          status: pendingStatus
        }
      });
    }
    setShowStatusConfirmModal(false);
    setPendingStatus(null);
  };
  const handleCancelStatusChange = () => {
    setShowStatusConfirmModal(false);
    setPendingStatus(null);
  };
  const getCurrentStatusConfig = () => {
    return criteriaStatuses.find(s => s.value === client.criteria.status) || criteriaStatuses[0];
  };
  const handleAddActivity = () => {
    // Stop recording if active
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      setInterimTranscript('');
    }
    // In a real app, this would save to backend
    console.log('New activity:', {
      mode: activityMode,
      ...newActivity
    });
    if (activityMode === 'log') {
      // Show success state after logging
      setActivityJustLogged(true);
    } else {
      // Close modal after scheduling
      setShowAddActivityModal(false);
      setNewActivity({
        type: 'call',
        date: '',
        time: '',
        note: ''
      });
      setActivityMode('log');
    }
  };
  const handleAddNote = () => {
    // Stop recording if active
    if (isRecordingNote && noteRecognitionRef.current) {
      noteRecognitionRef.current.stop();
      setIsRecordingNote(false);
      setInterimNoteTranscript('');
    }
    if (!noteText.trim()) {
      alert('Please enter a note');
      return;
    }
    // In a real app, this would save to backend
    console.log('New note:', noteText);
    // Show success state
    setNoteJustAdded(true);
  };
  const handleCloseNoteModal = () => {
    // Stop recording if active
    if (isRecordingNote && noteRecognitionRef.current) {
      noteRecognitionRef.current.stop();
      setIsRecordingNote(false);
      setInterimNoteTranscript('');
    }
    setShowAddNoteModal(false);
    setNoteJustAdded(false);
    setNoteText('');
  };
  const handleOpenAddNote = () => {
    setNoteText('');
    setNoteJustAdded(false);
    setShowAddNoteModal(true);
  };
  const handleScheduleAnother = (type: 'call' | 'meeting' | 'site-visit') => {
    setActivityJustLogged(false);
    setSelectedActivityType(type);
    setNewActivity({
      type: type,
      date: '',
      time: '',
      note: ''
    });
    setActivityMode('schedule');
    setActivityModalStep('form');
  };
  const handleAIAnalysis = () => {
    setShowAIModal(true);
    setAiAnalyzing(true);
    // Simulate AI analysis
    setTimeout(() => {
      setAiAnalyzing(false);
      setAiRecommendations({
        summary: `Based on ${client.name}'s profile, search criteria, and activity history, here are personalized insights and recommendations.`,
        insights: [{
          icon: Target,
          title: 'Client Readiness',
          description: 'High intent buyer - actively engaged with 3 site visits in the past week',
          color: 'green'
        }, {
          icon: TrendingUp,
          title: 'Budget Analysis',
          description: 'Budget range (2M-2.5M EGP) aligns well with current market inventory in New Cairo',
          color: 'blue'
        }, {
          icon: Lightbulb,
          title: 'Engagement Pattern',
          description: 'Most responsive during afternoon hours (2-5 PM). Schedule calls accordingly.',
          color: 'purple'
        }],
        recommendations: [{
          priority: 'high',
          title: 'Follow up on overdue call',
          description: 'Client has an overdue follow-up call from yesterday. Reach out today to maintain momentum.',
          action: 'Schedule Call'
        }, {
          priority: 'high',
          title: 'Share new villa listings',
          description: '2 new villas matching criteria just listed in 5th Settlement. High match percentage (95%).',
          action: 'Share Properties'
        }, {
          priority: 'medium',
          title: 'Expand search to Hyde Park',
          description: 'Based on activity history, client showed interest in Hyde Park properties. Consider expanding search.',
          action: 'Update Criteria'
        }, {
          priority: 'low',
          title: 'Schedule site visit',
          description: 'Client viewed 3 properties online. Time to schedule in-person visits for top matches.',
          action: 'Schedule Visit'
        }],
        nextBestAction: {
          title: 'Recommended Next Step',
          description: 'Call client today to discuss the 2 new high-match villas and schedule a site visit for this weekend.',
          confidence: 92
        }
      });
    }, 2500);
  };
  const handleCloseActivityModal = () => {
    // Stop recording if active
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      setInterimTranscript('');
    }
    setShowAddActivityModal(false);
    setActivityJustLogged(false);
    setNewActivity({
      type: 'call',
      date: '',
      time: '',
      note: ''
    });
    setActivityMode('log');
    setActivityModalStep('type');
  };
  const handleOpenAddActivity = (type: 'call' | 'meeting' | 'site-visit') => {
    setSelectedActivityType(type);
    setActivityMode('log');
    setNewActivity({
      type: type,
      date: '',
      time: '',
      note: ''
    });
    setActivityModalStep('mode');
    setShowAddActivityModal(true);
  };
  const handleSelectActivityType = (type: 'call' | 'meeting' | 'site-visit') => {
    setSelectedActivityType(type);
    setNewActivity({
      ...newActivity,
      type: type
    });
    setActivityModalStep('mode');
  };
  const handleSelectMode = (mode: 'log' | 'schedule') => {
    setActivityMode(mode);
    setActivityModalStep('form');
  };
  const handleBackInModal = () => {
    // Stop recording if active
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      setInterimTranscript('');
    }
    if (activityModalStep === 'form') {
      setActivityModalStep('mode');
    } else if (activityModalStep === 'mode') {
      setActivityModalStep('type');
    }
  };
  const getActivityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      call: 'Call',
      meeting: 'Meeting',
      'site-visit': 'Site Visit'
    };
    return labels[type] || type;
  };
  const toggleSection = (section: 'overdue' | 'upcoming' | 'past') => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const togglePropertySection = (section: 'new' | 'sent' | 'archived') => {
    setExpandedPropertySections({
      ...expandedPropertySections,
      [section]: !expandedPropertySections[section]
    });
  };
  const [matchedProperties, setMatchedProperties] = useState<Property[]>([{
    id: 1,
    title: 'Luxury Villa with Garden',
    price: '2,400,000 EGP',
    location: 'New Cairo, 5th Settlement',
    beds: 3,
    baths: 3,
    area: '320 sqm',
    matchPercentage: 95,
    status: 'new',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80'
  }, {
    id: 2,
    title: 'Modern Apartment',
    price: '2,100,000 EGP',
    location: 'New Cairo, Waterway',
    beds: 3,
    baths: 2,
    area: '185 sqm',
    matchPercentage: 88,
    status: 'new',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80'
  }, {
    id: 3,
    title: 'Duplex with Roof',
    price: '2,500,000 EGP',
    location: 'New Cairo, Hyde Park',
    beds: 4,
    baths: 3,
    area: '280 sqm',
    matchPercentage: 82,
    status: 'sent',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80'
  }, {
    id: 4,
    title: 'Penthouse with View',
    price: '3,200,000 EGP',
    location: 'New Cairo, Madinaty',
    beds: 3,
    baths: 2,
    area: '240 sqm',
    matchPercentage: 78,
    status: 'sent',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80'
  }, {
    id: 5,
    title: 'Villa in Compound',
    price: '2,800,000 EGP',
    location: 'New Cairo, Rehab',
    beds: 4,
    baths: 3,
    area: '350 sqm',
    matchPercentage: 72,
    status: 'archived',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'
  }]);
  const handleShareWhatsApp = (propertyId: number) => {
    console.log('Share via WhatsApp:', propertyId);
    alert('Opening WhatsApp...');
  };
  const handleMarkAsSent = (propertyId: number) => {
    setMatchedProperties(matchedProperties.map(prop => prop.id === propertyId ? {
      ...prop,
      status: 'sent' as const
    } : prop));
  };
  const handleArchive = (propertyId: number) => {
    setMatchedProperties(matchedProperties.map(prop => prop.id === propertyId ? {
      ...prop,
      status: 'archived' as const
    } : prop));
  };
  // Group properties by status
  const newProperties = matchedProperties.filter(p => p.status === 'new');
  const sentProperties = matchedProperties.filter(p => p.status === 'sent');
  const archivedProperties = matchedProperties.filter(p => p.status === 'archived');
  // Timeline events
  const [timelineEvents] = useState<TimelineEvent[]>([{
    id: 1,
    type: 'meeting',
    title: 'Follow-up Call',
    description: 'Discuss final property selection',
    date: 'Yesterday at 2:00 PM',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000),
    status: 'overdue',
    icon: Phone,
    color: 'red',
    user: {
      name: 'Sarah Ahmed',
      initials: 'SA'
    }
  }, {
    id: 2,
    type: 'meeting',
    title: 'Site Visit',
    description: 'Luxury Villa with Garden - New Cairo',
    date: 'Tomorrow at 3:00 PM',
    timestamp: new Date(Date.now() + 24 * 60 * 60 * 1000 + 3 * 60 * 60 * 1000),
    status: 'upcoming',
    icon: Calendar,
    color: 'green',
    user: {
      name: 'Mohamed Ali',
      initials: 'MA'
    }
  }, {
    id: 3,
    type: 'meeting',
    title: 'Property Viewing',
    description: 'Modern Apartment in Waterway',
    date: 'Jan 25 at 11:00 AM',
    timestamp: new Date(2024, 0, 25, 11, 0),
    status: 'upcoming',
    icon: Calendar,
    color: 'green',
    user: {
      name: 'Sarah Ahmed',
      initials: 'SA'
    }
  }, {
    id: 4,
    type: 'call',
    title: 'Phone Call',
    description: 'Discussed property requirements and budget',
    date: '2 hours ago',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    status: 'past',
    icon: Phone,
    color: 'blue',
    user: {
      name: 'Sarah Ahmed',
      initials: 'SA'
    }
  }, {
    id: 5,
    type: 'note',
    title: 'Note Added',
    description: 'Client prefers properties with garden and parking',
    date: 'Yesterday',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    status: 'past',
    icon: FileText,
    color: 'purple',
    user: {
      name: 'Karim Hassan',
      initials: 'KH'
    }
  }, {
    id: 6,
    type: 'status',
    title: 'Status Changed',
    description: 'Moved to "In Contact" stage',
    date: '2 days ago',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    status: 'past',
    icon: CheckCircle2,
    color: 'secondary',
    user: {
      name: 'System',
      initials: 'SY'
    }
  }, {
    id: 7,
    type: 'created',
    title: 'Lead Created',
    description: 'New lead from Website',
    date: '5 days ago',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    status: 'past',
    icon: Clock,
    color: 'gray',
    user: {
      name: 'System',
      initials: 'SY'
    }
  }]);
  // Group events by status
  const overdueEvents = timelineEvents.filter(e => e.status === 'overdue');
  const upcomingEvents = timelineEvents.filter(e => e.status === 'upcoming');
  const pastEvents = timelineEvents.filter(e => e.status === 'past');
  const getIconColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-50 text-blue-500',
      green: 'bg-green-50 text-green-500',
      purple: 'bg-purple-50 text-purple-500',
      secondary: 'bg-secondary/10 text-secondary',
      gray: 'bg-gray-50 text-gray-500',
      red: 'bg-red-50 text-red-500'
    };
    return colors[color] || colors.gray;
  };
  const renderTimelineSection = (title: string, events: TimelineEvent[], sectionKey: 'overdue' | 'upcoming' | 'past', badgeColor: string) => {
    if (events.length === 0) return null;
    return <div className="mb-6" data-id="element-464">
        <button onClick={() => toggleSection(sectionKey)} className="w-full flex items-center justify-between mb-3 px-2 py-1 hover:bg-white/50 rounded-lg transition-colors" data-id="element-465">
          <div className="flex items-center gap-2" data-id="element-466">
            <h3 className="text-sm font-bold text-neutral-text uppercase tracking-wide" data-id="element-467">
              {title}
            </h3>
            <span className={`${badgeColor} text-xs font-bold px-2 py-0.5 rounded-full`} data-id="element-468">
              {events.length}
            </span>
          </div>
          {expandedSections[sectionKey] ? <ChevronUp className="w-4 h-4 text-gray-400" data-id="element-469" /> : <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-470" />}
        </button>

        {expandedSections[sectionKey] && <div className="space-y-4" data-id="element-471">
            {events.map((event, index) => {
          const Icon = event.icon;
          return <div key={event.id} className="flex gap-4" data-id="element-472">
                  <div className="flex flex-col items-center" data-id="element-473">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getIconColor(event.color)}`} data-id="element-474">
                      <Icon className="w-5 h-5" data-id="element-475" />
                    </div>
                    {index < events.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-2 min-h-[40px]" data-id="element-476" />}
                  </div>
                  <div className="flex-1 pb-6" data-id="element-477">
                    <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm" data-id="element-478">
                      <div className="flex items-start justify-between mb-2" data-id="element-479">
                        <h3 className="font-semibold text-neutral-text flex-1" data-id="element-480">
                          {event.title}
                        </h3>
                        <div className="flex items-center gap-2 ml-2" data-id="element-481">
                          {event.user.avatar ? <img src={event.user.avatar} alt={event.user.name} className="w-6 h-6 rounded-full" data-id="element-482" /> : <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center" data-id="element-483">
                              <span className="text-[10px] font-semibold text-secondary" data-id="element-484">
                                {event.user.initials}
                              </span>
                            </div>}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-485">
                        {event.description}
                      </p>
                      <div className="flex items-center justify-between" data-id="element-486">
                        <p className="text-xs text-gray-400" data-id="element-487">{event.date}</p>
                        <p className="text-xs text-gray-500 font-medium" data-id="element-488">
                          {event.user.name}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>;
        })}
          </div>}
      </div>;
  };
  const renderPropertySection = (title: string, properties: Property[], sectionKey: 'new' | 'sent' | 'archived', badgeColor: string) => {
    if (properties.length === 0) return null;
    return <div className="mb-6" data-id="element-489">
        <button onClick={() => togglePropertySection(sectionKey)} className="w-full flex items-center justify-between mb-3 px-2 py-1 hover:bg-white/50 rounded-lg transition-colors" data-id="element-490">
          <div className="flex items-center gap-2" data-id="element-491">
            <h3 className="text-sm font-bold text-neutral-text uppercase tracking-wide" data-id="element-492">
              {title}
            </h3>
            <span className={`${badgeColor} text-xs font-bold px-2 py-0.5 rounded-full`} data-id="element-493">
              {properties.length}
            </span>
          </div>
          {expandedPropertySections[sectionKey] ? <ChevronUp className="w-4 h-4 text-gray-400" data-id="element-494" /> : <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-495" />}
        </button>

        {expandedPropertySections[sectionKey] && <div className="space-y-3" data-id="element-496">
            {properties.map(property => <PropertyCard key={property.id} {...property} onShareWhatsApp={() => handleShareWhatsApp(property.id)} onMarkAsSent={() => handleMarkAsSent(property.id)} onArchive={() => handleArchive(property.id)} data-id="element-497" />)}
          </div>}
      </div>;
  };
  const currentStatus = getCurrentStatusConfig();
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-498">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-499">
        {/* Header */}
        <div className="bg-primary px-4 py-6 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-500">
          <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-501">
            <ChevronLeft className="w-7 h-7" data-id="element-502" />
          </button>
          <h1 className="font-semibold text-xl text-white truncate max-w-[180px]" data-id="element-503">
            {client.name}
          </h1>
          <div className="flex gap-3 items-center" data-id="element-504">
            <button className="p-2.5 text-accent hover:bg-white/10 rounded-full transition-colors" data-id="element-505">
              <Phone className="w-6 h-6" data-id="element-506" />
            </button>
            <button className="p-2.5 text-accent hover:bg-white/10 rounded-full transition-colors" data-id="element-507">
              <MessageCircle className="w-6 h-6" data-id="element-508" />
            </button>

            <div className="relative" data-id="element-509">
              <button onClick={() => setShowMenu(!showMenu)} className="p-2.5 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-510">
                <MoreVertical className="w-6 h-6" data-id="element-511" />
              </button>

              {showMenu && <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} data-id="element-512" />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-20" data-id="element-513">
                    <button onClick={() => {
                  setShowContactModal(true);
                  setShowMenu(false);
                }} className="w-full px-4 py-3 text-left hover:bg-neutral-bg flex items-center gap-3 text-neutral-text" data-id="element-514">
                      <Eye className="w-4 h-4" data-id="element-515" />
                      <span className="text-sm font-medium" data-id="element-516">
                        View Contact Details
                      </span>
                    </button>
                    <button onClick={() => {
                  setShowMenu(false);
                }} className="w-full px-4 py-3 text-left hover:bg-neutral-bg flex items-center gap-3 text-neutral-text" data-id="element-517">
                      <Edit className="w-4 h-4" data-id="element-518" />
                      <span className="text-sm font-medium" data-id="element-519">
                        Edit Contact Details
                      </span>
                    </button>
                  </div>
                </>}
            </div>
          </div>
        </div>

        {/* Contact Details Modal */}
        {showContactModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" data-id="element-520">
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-2xl max-h-[85vh] overflow-y-auto" data-id="element-521">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-522">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-523">
                  Contact Details
                </h2>
                <button onClick={() => setShowContactModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-524">
                  <X className="w-5 h-5" data-id="element-525" />
                </button>
              </div>

              <div className="p-6" data-id="element-526">
                <div className="space-y-6" data-id="element-527">
                  <div className="flex items-start gap-4" data-id="element-528">
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 flex-shrink-0" data-id="element-529">
                      <Phone className="w-5 h-5" data-id="element-530" />
                    </div>
                    <div className="flex-1" data-id="element-531">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-532">Phone Number</p>
                      <p className="text-base font-semibold text-neutral-text" data-id="element-533">
                        {client.phone}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-534">
                    <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center text-purple-500 flex-shrink-0" data-id="element-535">
                      <Mail className="w-5 h-5" data-id="element-536" />
                    </div>
                    <div className="flex-1" data-id="element-537">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-538">
                        Email Address
                      </p>
                      <p className="text-base font-semibold text-neutral-text" data-id="element-539">
                        {client.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-540">
                    <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-541">
                      <Briefcase className="w-5 h-5" data-id="element-542" />
                    </div>
                    <div className="flex-1" data-id="element-543">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-544">Job Position</p>
                      <p className="text-base font-semibold text-neutral-text" data-id="element-545">
                        {client.position}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-546">
                    <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-green-500 flex-shrink-0" data-id="element-547">
                      <Globe className="w-5 h-5" data-id="element-548" />
                    </div>
                    <div className="flex-1" data-id="element-549">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-550">Lead Source</p>
                      <p className="text-base font-semibold text-neutral-text" data-id="element-551">
                        {client.source}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* Status Change Confirmation Modal */}
        {showStatusConfirmModal && pendingStatus && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-id="element-552">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl" data-id="element-553">
              <div className="p-6" data-id="element-554">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4" data-id="element-555">
                  <AlertCircle className="w-6 h-6 text-orange-600" data-id="element-556" />
                </div>
                <h3 className="text-lg font-bold text-neutral-text text-center mb-2" data-id="element-557">
                  Change Status?
                </h3>
                <p className="text-sm text-gray-600 text-center mb-6" data-id="element-558">
                  Are you sure you want to change the search criteria status
                  from{' '}
                  <span className="font-semibold" data-id="element-559">
                    {getCurrentStatusConfig().label}
                  </span>{' '}
                  to{' '}
                  <span className="font-semibold" data-id="element-560">
                    {criteriaStatuses.find(s => s.value === pendingStatus)?.label}
                  </span>
                  ?
                </p>
                <div className="flex gap-3" data-id="element-561">
                  <button onClick={handleCancelStatusChange} className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors" data-id="element-562">
                    Cancel
                  </button>
                  <button onClick={handleConfirmStatusChange} className="flex-1 py-2.5 px-4 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors" data-id="element-563">
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Edit Criteria Modal */}
        {showEditCriteriaModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" data-id="element-564">
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-2xl max-h-[85vh] overflow-y-auto" data-id="element-565">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-566">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-567">
                  Edit Search Criteria
                </h2>
                <button onClick={() => {
              setShowEditCriteriaModal(false);
              setEditForm(client.criteria);
            }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-568">
                  <X className="w-5 h-5" data-id="element-569" />
                </button>
              </div>

              <div className="p-6 space-y-4" data-id="element-570">
                <div data-id="element-571">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-572">
                    Property Group
                  </label>
                  <select value={editForm.group} onChange={e => setEditForm({
                ...editForm,
                group: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-573">
                    <option value="Residential" data-id="element-574">Residential</option>
                    <option value="Commercial" data-id="element-575">Commercial</option>
                  </select>
                </div>

                <div data-id="element-576">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-577">
                    Property Type
                  </label>
                  <select value={editForm.type} onChange={e => setEditForm({
                ...editForm,
                type: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-578">
                    <option value="Villa" data-id="element-579">Villa</option>
                    <option value="Apartment" data-id="element-580">Apartment</option>
                    <option value="Duplex" data-id="element-581">Duplex</option>
                    <option value="Penthouse" data-id="element-582">Penthouse</option>
                  </select>
                </div>

                <div data-id="element-583">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-584">
                    Budget Range
                  </label>
                  <input type="text" value={editForm.budget} onChange={e => setEditForm({
                ...editForm,
                budget: e.target.value
              })} placeholder="e.g., 2M - 2.5M EGP" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-585" />
                </div>

                <div data-id="element-586">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-587">
                    Bedrooms
                  </label>
                  <input type="number" value={editForm.beds} onChange={e => setEditForm({
                ...editForm,
                beds: parseInt(e.target.value)
              })} min="1" max="10" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-588" />
                </div>

                <div data-id="element-589">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-590">
                    Bathrooms
                  </label>
                  <input type="number" value={editForm.baths} onChange={e => setEditForm({
                ...editForm,
                baths: parseInt(e.target.value)
              })} min="1" max="10" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-591" />
                </div>

                <div data-id="element-592">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-593">
                    Interested Location
                  </label>
                  <input type="text" value={editForm.location} onChange={e => setEditForm({
                ...editForm,
                location: e.target.value
              })} placeholder="e.g., New Cairo, 5th Settlement" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-594" />
                </div>

                <button onClick={handleSaveCriteria} className="w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mt-6" data-id="element-595">
                  <Save className="w-5 h-5" data-id="element-596" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>}

        {/* Add Note Modal */}
        {showAddNoteModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" data-id="element-597">
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-2xl max-h-[85vh] flex flex-col overflow-hidden" data-id="element-598">
              <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0" data-id="element-599">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-600">
                  Add Note
                </h2>
                <button onClick={handleCloseNoteModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-601">
                  <X className="w-5 h-5" data-id="element-602" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto" data-id="element-603">
                <div className="p-6" data-id="element-604">
                  {!noteJustAdded ? <div className="space-y-4" data-id="element-605">
                      {/* Voice Input Button */}
                      <div className="flex items-center justify-between mb-2" data-id="element-606">
                        <label className="block text-sm font-medium text-neutral-text" data-id="element-607">
                          Note
                        </label>
                        <button onClick={toggleNoteRecording} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${isRecordingNote ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-608">
                          {isRecordingNote ? <>
                              <MicOff className="w-4 h-4" data-id="element-609" />
                              Stop Recording
                            </> : <>
                              <Mic className="w-4 h-4" data-id="element-610" />
                              Voice Input
                            </>}
                        </button>
                      </div>

                      {/* Recording indicator */}
                      {isRecordingNote && <div className="mb-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-id="element-611">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" data-id="element-612" />
                          <span data-id="element-613">Listening... Speak now</span>
                        </div>}

                      <div className="relative" data-id="element-614">
                        <textarea value={noteText + interimNoteTranscript} onChange={e => setNoteText(e.target.value)} placeholder="Type your note here or tap the mic button to speak..." rows={8} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none" data-id="element-615" />
                        {interimNoteTranscript && <div className="absolute bottom-3 left-3 right-3 text-xs text-gray-400 italic" data-id="element-616">
                            {interimNoteTranscript}
                          </div>}
                      </div>

                      <p className="text-xs text-gray-400" data-id="element-617">
                        Tip: Use the microphone to dictate your notes
                        hands-free. The transcript will be saved as text.
                      </p>

                      <button onClick={handleAddNote} disabled={!noteText.trim() && !interimNoteTranscript} className="w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-618">
                        <Save className="w-5 h-5" data-id="element-619" />
                        Save Note
                      </button>
                    </div> /* Success State */ : <div className="text-center py-8" data-id="element-620">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-621">
                        <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-622" />
                      </div>
                      <h3 className="text-xl font-bold text-neutral-text mb-2" data-id="element-623">
                        Note Added!
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-624">
                        Your note has been saved to the client's timeline.
                      </p>

                      <div className="space-y-3" data-id="element-625">
                        <button onClick={() => {
                    setNoteJustAdded(false);
                    setNoteText('');
                  }} className="w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3 rounded-lg transition-colors" data-id="element-626">
                          Add Another Note
                        </button>

                        <button onClick={handleCloseNoteModal} className="w-full bg-white hover:bg-gray-50 text-neutral-text font-semibold py-3 rounded-lg transition-colors border-2 border-gray-200" data-id="element-627">
                          Done
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </div>}

        {/* Add Activity Modal */}
        {showAddActivityModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" data-id="element-628">
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-2xl h-[85vh] flex flex-col overflow-hidden" data-id="element-629">
              <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0" data-id="element-630">
                <div className="flex items-center gap-3" data-id="element-631">
                  {activityModalStep !== 'type' && <button onClick={handleBackInModal} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-632">
                      <ChevronLeft className="w-5 h-5" data-id="element-633" />
                    </button>}
                  <h2 className="text-xl font-bold text-neutral-text" data-id="element-634">
                    {activityModalStep === 'type' && 'Select Activity Type'}
                    {activityModalStep === 'mode' && `${getActivityTypeLabel(selectedActivityType)}`}
                    {activityModalStep === 'form' && `${activityMode === 'log' ? 'Log Past' : 'Schedule New'} ${getActivityTypeLabel(selectedActivityType)}`}
                  </h2>
                </div>
                <button onClick={handleCloseActivityModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-635">
                  <X className="w-5 h-5" data-id="element-636" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto" data-id="element-637">
                <div className="p-6" data-id="element-638">
                  {/* Step 1: Activity Type Selection */}
                  {activityModalStep === 'type' && <div className="space-y-3" data-id="element-639">
                      <button onClick={() => handleSelectActivityType('call')} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-secondary hover:bg-secondary/5 transition-all" data-id="element-640">
                        <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-641">
                          <Phone className="w-6 h-6 text-blue-500" data-id="element-642" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-643">
                          <h3 className="font-semibold text-neutral-text" data-id="element-644">
                            Call
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-645">
                            Log or schedule a phone call
                          </p>
                        </div>
                      </button>

                      <button onClick={() => handleSelectActivityType('meeting')} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-secondary hover:bg-secondary/5 transition-all" data-id="element-646">
                        <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center" data-id="element-647">
                          <Calendar className="w-6 h-6 text-purple-500" data-id="element-648" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-649">
                          <h3 className="font-semibold text-neutral-text" data-id="element-650">
                            Meeting
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-651">
                            Log or schedule a meeting
                          </p>
                        </div>
                      </button>

                      <button onClick={() => handleSelectActivityType('site-visit')} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-secondary hover:bg-secondary/5 transition-all" data-id="element-652">
                        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center" data-id="element-653">
                          <Home className="w-6 h-6 text-green-500" data-id="element-654" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-655">
                          <h3 className="font-semibold text-neutral-text" data-id="element-656">
                            Site Visit
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-657">
                            Log or schedule a property visit
                          </p>
                        </div>
                      </button>
                    </div>}

                  {/* Step 2: Mode Selection (Log Past or Schedule New) */}
                  {activityModalStep === 'mode' && <div className="space-y-3" data-id="element-658">
                      <button onClick={() => handleSelectMode('log')} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-secondary hover:bg-secondary/5 transition-all" data-id="element-659">
                        <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-660">
                          <FileText className="w-6 h-6 text-blue-500" data-id="element-661" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-662">
                          <h3 className="font-semibold text-neutral-text" data-id="element-663">
                            Log Past{' '}
                            {getActivityTypeLabel(selectedActivityType)}
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-664">
                            Record a{' '}
                            {getActivityTypeLabel(selectedActivityType).toLowerCase()}{' '}
                            that already happened
                          </p>
                        </div>
                      </button>

                      <button onClick={() => handleSelectMode('schedule')} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-secondary hover:bg-secondary/5 transition-all" data-id="element-665">
                        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center" data-id="element-666">
                          <Calendar className="w-6 h-6 text-green-500" data-id="element-667" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-668">
                          <h3 className="font-semibold text-neutral-text" data-id="element-669">
                            Schedule New{' '}
                            {getActivityTypeLabel(selectedActivityType)}
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-670">
                            Plan a future{' '}
                            {getActivityTypeLabel(selectedActivityType).toLowerCase()}
                          </p>
                        </div>
                      </button>
                    </div>}

                  {/* Step 3: Form */}
                  {activityModalStep === 'form' && !activityJustLogged && <div className="space-y-4" data-id="element-671">
                      {/* Date and Time - Only for Schedule mode */}
                      {activityMode === 'schedule' && <div className="grid grid-cols-2 gap-3" data-id="element-672">
                          <div data-id="element-673">
                            <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-674">
                              Date
                            </label>
                            <input type="date" value={newActivity.date} onChange={e => setNewActivity({
                      ...newActivity,
                      date: e.target.value
                    })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-675" />
                          </div>

                          <div data-id="element-676">
                            <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-677">
                              Time
                            </label>
                            <input type="time" value={newActivity.time} onChange={e => setNewActivity({
                      ...newActivity,
                      time: e.target.value
                    })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-678" />
                          </div>
                        </div>}

                      {/* Note - Only for Log mode */}
                      {activityMode === 'log' && <div data-id="element-679">
                          <div className="flex items-center justify-between mb-2" data-id="element-680">
                            <label className="block text-sm font-medium text-neutral-text" data-id="element-681">
                              Note
                            </label>
                            <button onClick={toggleRecording} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${isRecording ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-682">
                              {isRecording ? <>
                                  <MicOff className="w-4 h-4" data-id="element-683" />
                                  Stop Recording
                                </> : <>
                                  <Mic className="w-4 h-4" data-id="element-684" />
                                  Voice Input
                                </>}
                            </button>
                          </div>

                          {/* Recording indicator */}
                          {isRecording && <div className="mb-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-id="element-685">
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" data-id="element-686" />
                              <span data-id="element-687">Listening... Speak now</span>
                            </div>}

                          <div className="relative" data-id="element-688">
                            <textarea value={newActivity.note + interimTranscript} onChange={e => setNewActivity({
                      ...newActivity,
                      note: e.target.value
                    })} placeholder="Add details about this activity... or tap the mic button to speak" rows={6} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none" data-id="element-689" />
                            {interimTranscript && <div className="absolute bottom-2 left-2 right-2 text-xs text-gray-400 italic" data-id="element-690">
                                {interimTranscript}
                              </div>}
                          </div>

                          <p className="text-xs text-gray-400 mt-1" data-id="element-691">
                            Tip: Use the microphone to dictate your notes
                            hands-free
                          </p>
                        </div>}

                      <button onClick={handleAddActivity} className="w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-692">
                        <Save className="w-5 h-5" data-id="element-693" />
                        {activityMode === 'log' ? `Log ${getActivityTypeLabel(selectedActivityType)}` : `Schedule ${getActivityTypeLabel(selectedActivityType)}`}
                      </button>
                    </div>}

                  {/* Success State - After Logging Activity */}
                  {activityModalStep === 'form' && activityJustLogged && <div className="text-center py-8" data-id="element-694">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-695">
                        <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-696" />
                      </div>
                      <h3 className="text-xl font-bold text-neutral-text mb-2" data-id="element-697">
                        Activity Logged!
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-698">
                        Your{' '}
                        {getActivityTypeLabel(selectedActivityType).toLowerCase()}{' '}
                        has been recorded successfully.
                      </p>

                      <div className="space-y-3" data-id="element-699">
                        <p className="text-sm font-medium text-neutral-text mb-3" data-id="element-700">
                          Schedule a follow-up activity:
                        </p>

                        <button onClick={() => handleScheduleAnother('call')} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all" data-id="element-701">
                          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-702">
                            <Phone className="w-5 h-5 text-blue-500" data-id="element-703" />
                          </div>
                          <span className="font-semibold text-neutral-text" data-id="element-704">
                            Schedule Call
                          </span>
                        </button>

                        <button onClick={() => handleScheduleAnother('meeting')} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition-all" data-id="element-705">
                          <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center" data-id="element-706">
                            <Calendar className="w-5 h-5 text-purple-500" data-id="element-707" />
                          </div>
                          <span className="font-semibold text-neutral-text" data-id="element-708">
                            Schedule Meeting
                          </span>
                        </button>

                        <button onClick={() => handleScheduleAnother('site-visit')} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-green-500 hover:bg-green-50 transition-all" data-id="element-709">
                          <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center" data-id="element-710">
                            <Home className="w-5 h-5 text-green-500" data-id="element-711" />
                          </div>
                          <span className="font-semibold text-neutral-text" data-id="element-712">
                            Schedule Site Visit
                          </span>
                        </button>

                        <button onClick={handleCloseActivityModal} className="w-full bg-white hover:bg-gray-50 text-neutral-text font-semibold py-3 rounded-lg transition-colors border-2 border-gray-200 mt-4" data-id="element-713">
                          Done
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </div>}

        <div className="space-y-6 pb-4" data-id="element-714">
          {/* Search Criteria Section */}
          <section className="bg-gradient-to-r from-teal-100 via-blue-50 to-purple-100 rounded-2xl p-6 shadow-sm" data-id="element-715">
            <div className="flex items-center justify-between mb-4" data-id="element-716">
              <h2 className="text-sm font-bold text-neutral-text uppercase tracking-wide" data-id="element-717">
                Search Criteria
              </h2>
              <div className="flex items-center gap-2" data-id="element-718">
                {/* Status Dropdown */}
                <div className="relative" data-id="element-719">
                  <button onClick={() => setShowStatusDropdown(!showStatusDropdown)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${currentStatus.bgColor} ${currentStatus.color}`} data-id="element-720">
                    <span data-id="element-721">{currentStatus.label}</span>
                    <ChevronDown className="w-3 h-3" data-id="element-722" />
                  </button>

                  {showStatusDropdown && <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} data-id="element-723" />
                      <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20" data-id="element-724">
                        {criteriaStatuses.map(status => <button key={status.value} onClick={() => handleStatusChange(status.value)} className={`w-full px-4 py-2 text-left text-sm hover:bg-neutral-bg transition-colors flex items-center gap-2 ${client.criteria.status === status.value ? 'bg-neutral-bg' : ''}`} data-id="element-725">
                            <span className={`w-2 h-2 rounded-full ${status.bgColor}`} data-id="element-726"></span>
                            <span className={status.color} data-id="element-727">{status.label}</span>
                          </button>)}
                      </div>
                    </>}
                </div>

                <button onClick={() => {
                setEditForm(client.criteria);
                setShowEditCriteriaModal(true);
              }} className="p-1.5 text-secondary hover:bg-secondary/10 rounded-lg transition-colors" data-id="element-728">
                  <Edit className="w-4 h-4" data-id="element-729" />
                </button>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-id="element-730">
              <div className="grid grid-cols-2 gap-4" data-id="element-731">
                <div className="flex items-start gap-3" data-id="element-732">
                  <Building2 className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-733" />
                  <div data-id="element-734">
                    <p className="text-xs text-gray-400" data-id="element-735">Property Group</p>
                    <p className="text-sm font-medium text-neutral-text" data-id="element-736">
                      {client.criteria.group}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-737">
                  <Home className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-738" />
                  <div data-id="element-739">
                    <p className="text-xs text-gray-400" data-id="element-740">Property Type</p>
                    <p className="text-sm font-medium text-neutral-text" data-id="element-741">
                      {client.criteria.type}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 col-span-2" data-id="element-742">
                  <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-743" />
                  <div data-id="element-744">
                    <p className="text-xs text-gray-400" data-id="element-745">Budget Range</p>
                    <p className="text-sm font-medium text-neutral-text" data-id="element-746">
                      {client.criteria.budget}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-747">
                  <Bed className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-748" />
                  <div data-id="element-749">
                    <p className="text-xs text-gray-400" data-id="element-750">Bedrooms</p>
                    <p className="text-sm font-medium text-neutral-text" data-id="element-751">
                      {client.criteria.beds}+
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-752">
                  <Bath className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-753" />
                  <div data-id="element-754">
                    <p className="text-xs text-gray-400" data-id="element-755">Bathrooms</p>
                    <p className="text-sm font-medium text-neutral-text" data-id="element-756">
                      {client.criteria.baths}+
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 col-span-2" data-id="element-757">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-758" />
                  <div data-id="element-759">
                    <p className="text-xs text-gray-400" data-id="element-760">Interested Location</p>
                    <p className="text-sm font-medium text-neutral-text" data-id="element-761">
                      {client.criteria.location}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Tabs */}
          <section data-id="element-762">
            <div className="flex gap-2 bg-neutral-bg px-4 py-3 sticky top-[72px] z-10" data-id="element-763">
              <button onClick={() => setActiveTab('timeline')} className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg transition-all ${activeTab === 'timeline' ? 'bg-secondary text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-764">
                Timeline
              </button>
              <button onClick={() => setActiveTab('matched')} className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg transition-all ${activeTab === 'matched' ? 'bg-secondary text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-765">
                Matched Properties
              </button>
            </div>

            {/* Tab Content */}
            <div className="px-4 py-4" data-id="element-766">
              {activeTab === 'timeline' ? <div data-id="element-767">
                  {/* Activity Type Buttons */}
                  <div className="grid grid-cols-4 gap-2 mb-6" data-id="element-768">
                    <button onClick={() => handleOpenAddActivity('call')} className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all" data-id="element-769">
                      <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-770">
                        <Phone className="w-4 h-4 text-blue-500" data-id="element-771" />
                      </div>
                      <span className="text-xs font-semibold text-neutral-text" data-id="element-772">
                        Call
                      </span>
                    </button>

                    <button onClick={() => handleOpenAddActivity('meeting')} className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition-all" data-id="element-773">
                      <div className="w-9 h-9 rounded-full bg-purple-50 flex items-center justify-center" data-id="element-774">
                        <Calendar className="w-4 h-4 text-purple-500" data-id="element-775" />
                      </div>
                      <span className="text-xs font-semibold text-neutral-text" data-id="element-776">
                        Meeting
                      </span>
                    </button>

                    <button onClick={() => handleOpenAddActivity('site-visit')} className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 border-gray-200 hover:border-green-500 hover:bg-green-50 transition-all" data-id="element-777">
                      <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center" data-id="element-778">
                        <Home className="w-4 h-4 text-green-500" data-id="element-779" />
                      </div>
                      <span className="text-xs font-semibold text-neutral-text" data-id="element-780">
                        Site Visit
                      </span>
                    </button>

                    <button onClick={handleOpenAddNote} className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all" data-id="element-781">
                      <div className="w-9 h-9 rounded-full bg-orange-50 flex items-center justify-center" data-id="element-782">
                        <FileText className="w-4 h-4 text-orange-500" data-id="element-783" />
                      </div>
                      <span className="text-xs font-semibold text-neutral-text" data-id="element-784">
                        Note
                      </span>
                    </button>
                  </div>

                  {renderTimelineSection('Overdue', overdueEvents, 'overdue', 'bg-red-100 text-red-600')}

                  {renderTimelineSection('Upcoming', upcomingEvents, 'upcoming', 'bg-green-100 text-green-600')}

                  {renderTimelineSection('Past', pastEvents, 'past', 'bg-gray-100 text-gray-600')}
                </div> : <div data-id="element-785">
                  {renderPropertySection('New', newProperties, 'new', 'bg-accent/20 text-accent-dark')}

                  {renderPropertySection('Sent', sentProperties, 'sent', 'bg-secondary/20 text-secondary')}

                  {renderPropertySection('Archived', archivedProperties, 'archived', 'bg-gray-100 text-gray-600')}
                </div>}
            </div>
          </section>
        </div>

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} data-id="element-786" />

        {/* AI Assistant Floating Button */}
        <button onClick={handleAIAnalysis} className="fixed top-1/2 -translate-y-1/2 right-6 w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform z-40 animate-pulse" data-id="element-787">
          <Sparkles className="w-6 h-6" data-id="element-788" />
        </button>

        {/* AI Analysis Modal */}
        {showAIModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" data-id="element-789">
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-2xl max-h-[85vh] overflow-y-auto" data-id="element-790">
              <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 flex items-center justify-between" data-id="element-791">
                <div className="flex items-center gap-3" data-id="element-792">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-793">
                    <Sparkles className="w-5 h-5 text-white" data-id="element-794" />
                  </div>
                  <div data-id="element-795">
                    <h2 className="text-lg font-bold text-white" data-id="element-796">
                      AI Assistant
                    </h2>
                    <p className="text-xs text-white/80" data-id="element-797">
                      Powered by Magic Patterns
                    </p>
                  </div>
                </div>
                <button onClick={() => {
              setShowAIModal(false);
              setAiRecommendations(null);
            }} className="p-2 text-white hover:bg-white/20 rounded-full transition-colors" data-id="element-798">
                  <X className="w-5 h-5" data-id="element-799" />
                </button>
              </div>

              <div className="p-6" data-id="element-800">
                {aiAnalyzing ? <div className="text-center py-12" data-id="element-801">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse" data-id="element-802">
                      <Sparkles className="w-8 h-8 text-purple-500" data-id="element-803" />
                    </div>
                    <h3 className="text-lg font-bold text-neutral-text mb-2" data-id="element-804">
                      Analyzing Client Profile...
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-805">
                      Reading search criteria, activities, and notes
                    </p>
                  </div> : aiRecommendations ? <div className="space-y-6" data-id="element-806">
                    {/* Summary */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100" data-id="element-807">
                      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-808">
                        {aiRecommendations.summary}
                      </p>
                    </div>

                    {/* Key Insights */}
                    <div data-id="element-809">
                      <h3 className="text-sm font-bold text-neutral-text uppercase tracking-wide mb-3" data-id="element-810">
                        Key Insights
                      </h3>
                      <div className="space-y-3" data-id="element-811">
                        {aiRecommendations.insights.map((insight: any, index: number) => {
                    const Icon = insight.icon;
                    const colorClasses = {
                      green: 'bg-green-50 text-green-500',
                      blue: 'bg-blue-50 text-blue-500',
                      purple: 'bg-purple-50 text-purple-500'
                    };
                    return <div key={index} className="flex gap-3 bg-white rounded-lg border border-gray-100 p-3" data-id="element-812">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[insight.color as keyof typeof colorClasses]}`} data-id="element-813">
                                  <Icon className="w-5 h-5" data-id="element-814" />
                                </div>
                                <div className="flex-1" data-id="element-815">
                                  <h4 className="font-semibold text-neutral-text text-sm mb-1" data-id="element-816">
                                    {insight.title}
                                  </h4>
                                  <p className="text-xs text-gray-600" data-id="element-817">
                                    {insight.description}
                                  </p>
                                </div>
                              </div>;
                  })}
                      </div>
                    </div>

                    {/* Next Best Action */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200" data-id="element-818">
                      <div className="flex items-start gap-3 mb-3" data-id="element-819">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-820">
                          <Target className="w-5 h-5 text-white" data-id="element-821" />
                        </div>
                        <div className="flex-1" data-id="element-822">
                          <h3 className="font-bold text-neutral-text mb-1" data-id="element-823">
                            {aiRecommendations.nextBestAction.title}
                          </h3>
                          <p className="text-sm text-gray-700 mb-2" data-id="element-824">
                            {aiRecommendations.nextBestAction.description}
                          </p>
                          <div className="flex items-center gap-2" data-id="element-825">
                            <div className="flex-1 bg-white rounded-full h-2 overflow-hidden" data-id="element-826">
                              <div className="bg-green-500 h-full rounded-full transition-all" style={{
                          width: `${aiRecommendations.nextBestAction.confidence}%`
                        }} data-id="element-827" />
                            </div>
                            <span className="text-xs font-semibold text-green-600" data-id="element-828">
                              {aiRecommendations.nextBestAction.confidence}%
                              confidence
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Recommendations */}
                    <div data-id="element-829">
                      <h3 className="text-sm font-bold text-neutral-text uppercase tracking-wide mb-3" data-id="element-830">
                        Recommendations
                      </h3>
                      <div className="space-y-2" data-id="element-831">
                        {aiRecommendations.recommendations.map((rec: any, index: number) => {
                    const priorityColors = {
                      high: 'bg-red-100 text-red-700 border-red-200',
                      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                      low: 'bg-gray-100 text-gray-700 border-gray-200'
                    };
                    return <div key={index} className="bg-white rounded-lg border border-gray-100 p-4" data-id="element-832">
                                <div className="flex items-start justify-between mb-2" data-id="element-833">
                                  <h4 className="font-semibold text-neutral-text text-sm flex-1" data-id="element-834">
                                    {rec.title}
                                  </h4>
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${priorityColors[rec.priority as keyof typeof priorityColors]}`} data-id="element-835">
                                    {rec.priority.toUpperCase()}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mb-3" data-id="element-836">
                                  {rec.description}
                                </p>
                                <button className="text-xs font-semibold text-secondary hover:text-secondary-dark transition-colors" data-id="element-837">
                                  {rec.action} →
                                </button>
                              </div>;
                  })}
                      </div>
                    </div>

                    {/* Action Button */}
                    <button onClick={() => {
                setShowAIModal(false);
                setAiRecommendations(null);
              }} className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 rounded-lg transition-all" data-id="element-838">
                      Got it, thanks!
                    </button>
                  </div> : null}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}